import React from 'react';
import classNames from 'classnames';
import { Tag as AntDTag, TagProps as AntDTagProps } from 'antd';

import './index.less';
import { Colors } from '../Button';

interface Props extends Omit<AntDTagProps, 'color'> {
  disabled?: boolean;
  rounded?: boolean;
  error?: boolean;
  selected?: boolean;
  size?: 'small' | 'middle' | 'large';
  variant?: 'contained' | 'tertiary' | 'outlined';
  dashed?: boolean;
  color?: string;
}

export type { Props as ChipProps };

const colorMap = {
  success: 'success',
  secondary: 'secondary',
  default: 'gray',
  error: 'error',
  primary: 'primary',
  info: 'info',
  warning: 'warning',
} as Record<Colors, string>;

export default function Chip({
  icon,
  rounded = true,
  error = false,
  selected = false,
  disabled = false,
  dashed = false,
  variant = 'contained',
  size = 'middle',
  className = '',
  color = 'default',
  ...props
}: Props) {
  const convertedColor = colorMap[color as Colors] || color;
  return (
    <AntDTag
      tabIndex={0}
      icon={icon}
      className={classNames(
        'sl-chip tw-mr-0',
        {
          'tw-rounded-[999px]': rounded,
          'tw-px-2 tw-text-caption-2': size === 'small',
          'tw-px-2 tw-py-1 tw-text-caption-2': size === 'middle',
          'tw-py-2 tw-px-3 tw-text-body-3': size === 'large',
          [`tw-border-1 hover:tw-bg-${convertedColor}-200 active:tw-bg-${convertedColor}-300 tw-text-${convertedColor}-700`]:
            !selected && !disabled && !error,
          [`tw-border-dashed tw-border-${convertedColor}`]: dashed,
          [`tw-border-${convertedColor}-100`]: variant !== 'outlined',
          [`tw-border-${convertedColor}`]: variant !== 'tertiary',
          [`tw-bg-${convertedColor}-100`]: variant === 'tertiary',
          [`tw-bg-${convertedColor}`]: variant === 'contained',
          'tw-bg-transparent': variant === 'outlined',
          'tw-border-error-100 tw-bg-error-100 hover:tw-bg-error-200 active:tw-bg-error-300 tw-text-gray-700':
            !selected && !disabled && error,
          'tw-border-gray-100 tw-bg-gray-100 tw-text-gray-400': !selected && disabled,
          'tw-cursor-pointer': !disabled,
          'tw-border-secondary tw-bg-secondary tw-text-white': selected && !disabled,
          'tw-border-gray-500 tw-bg-gray-500 tw-text-gray-100': selected && disabled,
        },
        className
      )}
      {...props}
    />
  );
}
